import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/lib/onboardingContext";
import { AuthProvider } from "@/lib/authContext";
import { SubscriptionProvider } from "@/lib/subscriptionContext";
import { I18nProvider } from "@/lib/i18nContext";
import { LocationProvider } from "@/lib/locationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureGate from "@/components/FeatureGate";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Locations from "./pages/Locations";
import MyLocations from "./pages/MyLocations";
import Timeline from "./pages/Timeline";
import Permits from "./pages/Permits";
import BudgetPage from "./pages/Budget";
import Checklist from "./pages/Checklist";
import Assistant from "./pages/Assistant";
import Pricing from "./pages/Pricing";
import Billing from "./pages/Billing";
import Enterprise from "./pages/Enterprise";
import Forecast from "./pages/Forecast";
import PlanPreview from "./pages/PlanPreview";
import Affiliates from "./pages/Affiliates";
import SharedRoadmap from "./pages/SharedRoadmap";
import CostCalculator from "./pages/CostCalculator";
import AdminListings from "./pages/AdminListings";
import Equipment from "./pages/Equipment";
import Inventory from "./pages/Inventory";
import StaffRota from "./pages/StaffRota";
import Operations from "./pages/Operations";
import OperationsDashboard from "./pages/OperationsDashboard";
import Checkout from "./pages/Checkout";
import Unsubscribe from "./pages/Unsubscribe";
import SupportedCities from "./pages/SupportedCities";
import Admin from "./pages/Admin";
import Terms from "./pages/Terms";
import StripeFlowChecklist from "./pages/StripeFlowChecklist";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <I18nProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <LocationProvider>
              <AppProvider>
                <BrowserRouter>
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/enterprise" element={<Enterprise />} />
                    <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/locations" element={<ProtectedRoute><FeatureGate feature="locations.unlimited" featureName="Location browser"><Locations /></FeatureGate></ProtectedRoute>} />
                    <Route path="/my-locations" element={<ProtectedRoute><FeatureGate feature="multiLocation" featureName="My Locations"><MyLocations /></FeatureGate></ProtectedRoute>} />
                    <Route path="/timeline" element={<ProtectedRoute><FeatureGate feature="openingPlan" featureName="Opening Timeline"><Timeline /></FeatureGate></ProtectedRoute>} />
                    <Route path="/permits" element={<ProtectedRoute><Permits /></ProtectedRoute>} />
                    <Route path="/budget" element={<ProtectedRoute><FeatureGate feature="budget" featureName="Budget Planner"><BudgetPage /></FeatureGate></ProtectedRoute>} />
                    <Route path="/checklist" element={<ProtectedRoute><Checklist /></ProtectedRoute>} />
                    <Route path="/assistant" element={<ProtectedRoute><FeatureGate feature="aiAdvisor" featureName="AI Business Advisor"><Assistant /></FeatureGate></ProtectedRoute>} />
                    <Route path="/forecast" element={<ProtectedRoute><FeatureGate feature="forecast" featureName="Revenue Forecast"><Forecast /></FeatureGate></ProtectedRoute>} />
                    <Route path="/plan" element={<ProtectedRoute><PlanPreview /></ProtectedRoute>} />
                    <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                    <Route path="/affiliates" element={<Affiliates />} />
                    <Route path="/shared" element={<SharedRoadmap />} />
                    <Route path="/calculator" element={<ProtectedRoute><CostCalculator /></ProtectedRoute>} />
                    <Route path="/admin/listings" element={<ProtectedRoute><AdminListings /></ProtectedRoute>} />
                    <Route path="/equipment" element={<ProtectedRoute><FeatureGate feature="equipment" featureName="Equipment Planner"><Equipment /></FeatureGate></ProtectedRoute>} />
                    <Route path="/inventory" element={<ProtectedRoute><FeatureGate feature="inventory" featureName="Inventory"><Inventory /></FeatureGate></ProtectedRoute>} />
                    <Route path="/staff-rota" element={<ProtectedRoute><FeatureGate feature="staffRota" featureName="Staff Rota"><StaffRota /></FeatureGate></ProtectedRoute>} />
                    <Route path="/operations" element={<ProtectedRoute><FeatureGate feature="operations" featureName="Operations"><Operations /></FeatureGate></ProtectedRoute>} />
                    <Route path="/dashboard/operations" element={<ProtectedRoute><OperationsDashboard /></ProtectedRoute>} />
                    <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                    <Route path="/unsubscribe" element={<Unsubscribe />} />
                    <Route path="/supported-cities" element={<SupportedCities />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/stripe-flow-checklist" element={<ProtectedRoute><StripeFlowChecklist /></ProtectedRoute>} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </BrowserRouter>
              </AppProvider>
            </LocationProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </I18nProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
